import { useRef } from "react";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  CsvExportModule,
  ColDef,
  GridOptions,
  ModuleRegistry,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  FirstDataRenderedEvent,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  ModelUpdatedEvent,
  CellValueChangedEvent,
} from "ag-grid-community";
import { SetFilterModule } from "ag-grid-enterprise";

import "ag-grid-enterprise"; // Enable Enterprise features
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import classNames from "classnames";
import "ag-grid-enterprise";
import "./Grid.css"; // Your custom styles
import Button from "../button/button";

// Register AG Grid modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  SetFilterModule,
]);

type GridProps<T> = {
  columnDefs: ColDef<T>[];
  rowData: T[];
  onRowClick?: (data: T) => void;
  // AG Grid column events
  onCellValueChanged?: (event: CellValueChangedEvent<T>) => void; // <-- ADD THIS

  onColumnVisible?: (event: ColumnVisibleEvent) => void;
  onColumnResized?: (event: ColumnResizedEvent) => void;
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  onSelectionChanged?: (selectedRows: T[]) => void;
  rowSelection?: "single" | "multiple";
  height?: string;
  theme?: "ag-theme-alpine" | "ag-theme-quartz";
  onGridReady?: AgGridReactProps["onGridReady"];
  onCellClicked?: (params: any) => void;
  defaultColDef?: Partial<ColDef<T>>;
  getRowId?: (params: { data: T }) => string; // **Enforce string return here**\
  onFilterChanged?: (params: any) => void;

  onFirstDataRendered?: (params: FirstDataRenderedEvent<T>) => void;
  onModelUpdated?: (params: ModelUpdatedEvent<T>) => void;
};

function Grid<T>({
  columnDefs,
  rowData,
  onRowClick,
  onCellClicked,
  onSelectionChanged,
  onFilterChanged,
  onColumnVisible,
  onColumnResized,
  onColumnMoved,
  rowSelection = "single",
  height = "88vh",
  theme = "ag-theme-alpine",
  defaultColDef: userDefaultColDef,
  onGridReady,
  getRowId,
  onFirstDataRendered,
  onModelUpdated,
  ...rest
}: GridProps<T>) {
  const gridRef = useRef<AgGridReact<T>>(null);

  // Handle double-click event on rows
  const handleRowDoubleClick = (event: RowDoubleClickedEvent<T>) => {
    if (onRowClick && event.data) {
      onRowClick(event.data);
    }
  };

  // Handle selection change event
  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    if (onSelectionChanged) {
      const selectedRows = event.api.getSelectedRows() as T[];
      onSelectionChanged(selectedRows);
    }
  };

  // Default column definition (can be overridden)
  const defaultColDef: Partial<ColDef<T>> = {
    filter: true,
    sortable: true,
    resizable: true,
    floatingFilter: true,

    tooltipValueGetter: (params) => params.value,
    ...userDefaultColDef,
  };

  // Clear filters button handler
  const clearFilters = () => {
    gridRef.current?.api.setFilterModel(null);
  };

  // Status bar configuration
  const statusBar = {
    statusPanels: [
      { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
      { statusPanel: "agTotalRowCountComponent", align: "center" },
      { statusPanel: "agFilteredRowCountComponent" },
      { statusPanel: "agSelectedRowCountComponent" },
      { statusPanel: "agAggregationComponent" },
    ],
  };

  // Add checkbox selection column if multiple selection enabled
  const enhancedColumnDefs: ColDef<T>[] =
    rowSelection === "multiple"
      ? [
          {
            headerName: "",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            width: 50,
            pinned: "left",
            sortable: false,
            filter: false,
          },
          ...columnDefs,
        ]
      : columnDefs;

  // Grid options
  const gridOptions: GridOptions<T> = {
    enableCharts: true,
    rowSelection,
    rowMultiSelectWithClick: true,
    animateRows: true,

    suppressRowClickSelection: rowSelection === "multiple",
    enableRangeSelection: true,
    enableCellTextSelection: true,
    groupDefaultExpanded: 0,
    rowGroupPanelShow: "always",
    rowDragManaged: true,
    suppressAggFuncInHeader: true,
    suppressRowHoverHighlight: false,
    pivotPanelShow: "always",
    sideBar: "columns",
    clipboardDelimiter: ",",
    suppressDragLeaveHidesColumns: true,
    alwaysShowHorizontalScroll: false,
    suppressHorizontalScroll: false,
    chartThemeOverrides: {
      common: {
        title: {
          enabled: true,
          text: "AG Grid Chart",
        },
        background: {
          fill: "#f8f9fa",
        },
      },
    },
    getRowStyle: (params) => {
      const index = params.node.rowIndex;
      if (index === null) return undefined;
      return {
        backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9f9f9",
        fontSize: "14px",
      };
    },
  };

  return (
    <div
      className={classNames(theme)}
      style={{ height: height, width: "100%", paddingBottom: "50px" }}
    >
      <Button onClick={clearFilters} className="clear-filters-button">
        Clear Filters
      </Button>
      <AgGridReact<T>
        ref={gridRef}
        rowData={rowData}
        columnDefs={enhancedColumnDefs}
        defaultColDef={defaultColDef}
        pagination={true}
        paginationPageSize={50}
        onRowDoubleClicked={handleRowDoubleClick}
        onCellClicked={onCellClicked}
        onSelectionChanged={handleSelectionChanged}
        statusBar={statusBar}
        rowGroupPanelShow="always"
        suppressRowClickSelection={true}
        onColumnResized={onColumnResized}
        onColumnMoved={onColumnMoved}
        onColumnVisible={onColumnVisible}
        sideBar="columns"
        gridOptions={gridOptions}
        enableBrowserTooltips={true}
        suppressMenuHide={false}
        enableCellTextSelection={true}
        suppressMovableColumns={false}
        onFilterChanged={onFilterChanged}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onModelUpdated={onModelUpdated}
        getRowId={
          getRowId
            ? (params) => String(getRowId(params)) // Ensure string return
            : (params) => String((params.data as any).ticketId) // default getRowId fallback
        }
        {...rest} // Spread any other AgGridReact props
      />
    </div>
  );
}

export default Grid;
